import { Collection } from './../types/collection';
import { reduce } from '../collections/reduce';
import { _merge } from './_merge';

/**
 * Takes a destination object and source objects and recursively merges source objects onto destination object.
 * NB this method mutates `source` object.
 *
 * ```ts
 * const a = {
 *   prop1: 1
 * };
 *
 * const b = {
 *   prop2: 2
 * };
 *
 * const c = {
 *   prop3: 3
 * }
 *
 * tt.merge(a, b, c);
 * // a === { prop1: 1, prop2: 2, prop3: 3 }
 * ```
 *
 * @param dest The object onto which to merge properties from `sources`.
 * @param sources The objects whose properties should be merged with `dest`.
 */
export function merge<T1, T2>(dest: T1, source: T2): T1 & T2;
export function merge<T1, T2>(dest: T1[], source: T2[]): (T1 & T2)[];
export function merge<T1, T2, T3>(dest: T1, source: T2, source2: T3): T1 & T2 & T3;
export function merge<T1, T2, T3>(dest: T1[], source: T2[], source2: T3[]): (T1 & T2 & T3)[];
export function merge<T1, T2, T3, T4>(dest: T1, source: T2, source2: T3, source3: T4): T1 & T2 & T3 & T4;
export function merge<T1, T2, T3, T4>(dest: T1[], source: T2[], source2: T3[], source3: T4[]): (T1 & T2 & T3 & T4)[];
export function merge<T1, T2, T3, T4, T5>(
  dest: T1,
  source: T2,
  source2: T3,
  source3: T4,
  source4: T5
): T1 & T2 & T3 & T4 & T5;
export function merge<T1, T2, T3, T4, T5>(
  dest: T1[],
  source: T2[],
  source2: T3[],
  source3: T4[],
  source4: T5[]
): (T1 & T2 & T3 & T4 & T5)[];
export function merge(dest: Collection<unknown>, ...sources: Collection<unknown>[]): Collection<unknown> {
  return reduce(
    _merge.bind(undefined, () => undefined),
    dest,
    sources
  );
}
